import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import {
  Plus,
  Calendar,
  Pencil,
  MoreVertical,
  Star,
  Grid3x3,
  Table2,
  Search,
  User,
  ChevronLeft,
  ChevronRight,
  Filter,
  ArrowDownWideNarrow,
  ArrowUpWideNarrow,
  BookOpenText,
  LucidePlayCircle,
  Briefcase,
  CircleUser,
  EyeIcon,
  UserPlus,
  CheckCheckIcon,
} from "lucide-react";
import DataTable from "../components/ui/DataTable";
import MuiSelect from "../components/MuiSelect";
import ConfirmDialog from "../components/ui/ConfirmDialog";
import { projectService } from "../services/projectService";
import CardSkeleton from "../components/skeletons/CardSkeleton";
import { formatName, getInitials, formatDate } from "../utils/formatters";
import {
  storeClientId,
  getStoredClientId,
  clearStoredClientId,
} from "../utils/tokenUtils";
import { useCan } from "../hooks/useCan";
import toast from "react-hot-toast";
import ProjectTitleCard from "../components/ProjectTitleCard";
import CreateSampleProjectModal from "../components/CreateSampleProjectModal";
import CustomFilter from "../components/ui/CustomFilter";
import { Button } from "../components/ui/button";
import { Stack } from "@mui/material";
import ProjectPageEmptyState from "../components/testcase/ProjectPageEmptyState";
import { useDebounce } from "../hooks/useDebounce";
import { filterService } from "../services/filterService";

const ProjectsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [searchParams] = useSearchParams();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sortBy, setSortBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");

  // View mode: 'table' or 'grid'
  const [viewMode, setViewMode] = useState("table");

  // Search and filters (searchTerm = immediate input; debounced drives API)
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [isActiveFilter, setIsActiveFilter] = useState(true);

  // Filter states
  const [showFilter, setShowFilter] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState({});

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalProjects, setTotalProjects] = useState(0);
  const itemsPerPage = 10;

  // Modal states
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [showSampleProjectModal, setShowSampleProjectModal] = useState(false);

  // Delete states
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletingProject, setDeletingProject] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Inactive states
  const [showInactiveDialog, setShowInactiveDialog] = useState(false);
  const [inactivatingProject, setInactivatingProject] = useState(null);
  const [inactiveLoading, setInactiveLoading] = useState(false);

  // Activate states
  const [showActivateDialog, setShowActivateDialog] = useState(false);
  const [activatingProject, setActivatingProject] = useState(null);
  const [activateLoading, setActivateLoading] = useState(false);

  // Action menu states
  const [actionMenuOpen, setActionMenuOpen] = useState(null);
  const [menuPosition, setMenuPosition] = useState({
    top: 0,
    right: 0,
    showAbove: false,
  });
  const actionButtonRefs = useRef({});
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);
  const [completingProject, setCompletingProject] = useState(null);
  const [completeLoading, setCompleteLoading] = useState(false);
  const [filters, setFilters] = useState([]);

  // Client context
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [selectedClientIsActive, setSelectedClientIsActive] = useState(true);

  // Check permissions using useCan hook
  const { can: canCreateProject, loading: permissionsLoading } = useCan(
    "project",
    "add_edit",
  );
  const { can: canEditProject } = useCan("project", "add_edit");
  const { can: canDeleteProject } = useCan("project", "permanently_delete");
  const { can: canInactiveProject } = useCan("project", "inactive");
  const { can: canViewTestCases } = useCan("test_suits_cases_sections", "view");
  const { can: canUserAddEdit } = useCan("user", "add_edit");

  // Legacy check for backward compatibility - check both role_name and role, normalize them
  const userRole = user?.role_name || user?.role;
  const normalizeRole = (role) =>
    role ? role.toLowerCase().replace(/[_\s-]/g, "-") : null;
  const normalizedUserRole = normalizeRole(userRole);
  // Allow access if: has permission OR is in allowed roles (even if permissions are still loading)
  const allowedRoles = [
    "chief-admin",
    "admin",
    "project-manager",
    "project_manager",
    "test-manager",
    "test_manager",
  ];
  const hasAllowedRole = allowedRoles.includes(normalizedUserRole);
  const canManageProjects = canCreateProject || hasAllowedRole;
  const isUserChiefAdminRole = normalizedUserRole === "chief-admin";
  const isUserAdminRole = normalizedUserRole === "admin";
  const canInviteUser =
    isUserChiefAdminRole || isUserAdminRole || canUserAddEdit;

  /** Same client resolution as project list / create flows (URL → stored → JWT). */
  const sampleEffectiveClientId = useMemo(() => {
    const urlClientId = searchParams.get("client_id");
    const storedClientId = getStoredClientId();
    const role = user?.role_name || user?.role;
    const normalized = role ? role.toLowerCase().replace(/[_\s-]/g, "-") : null;
    const isChiefAdmin = normalized === "chief-admin";
    if (isChiefAdmin && urlClientId) return urlClientId;
    if (isChiefAdmin && !urlClientId) {
      return storedClientId || user?.client_id || null;
    }
    return user?.client_id || null;
  }, [searchParams, user?.role_name, user?.role, user?.client_id]);

  const fetchFilters = useCallback(async () => {
    try {
      const response = await filterService.fetchDistinctValues(
        "project",
        sampleEffectiveClientId,
      );
      const filterConfig = { sections: [] };
      for (const filter of response.data) {
        filterConfig.sections.push({
          key: filter.filterKey,
          label: filter.filterTitle,
          type: filter.filterType,
          options: [...filter.filterValues],
        });
      }
      setFilters(filterConfig);
    } catch (e) {
      toast.error("Failed to fetch filters");
    }
  }, [sampleEffectiveClientId]);

  // Filter configuration (mock data for now)
  const filterConfig = {
    sections: [
      {
        key: "createdBy",
        label: "Created By User",
        type: "checkbox",
        options: [
          { label: "John Doe", value: "john.doe@example.com" },
          { label: "Jane Smith", value: "jane.smith@example.com" },
          { label: "Mike Johnson", value: "mike.johnson@example.com" },
          { label: "Sarah Williams", value: "sarah.williams@example.com" },
          { label: "Admin User", value: "admin@example.com" },
        ],
      },
      {
        key: "project_type",
        label: "Project Type",
        type: "checkbox",
        options: [
          { label: "Test", value: "test" },
          { label: "Production", value: "production" },
        ],
      },
      {
        key: "updatedBy",
        label: "Updated By User",
        type: "checkbox",
        options: [
          { label: "John Doe", value: "john.doe@example.com" },
          { label: "Jane Smith", value: "jane.smith@example.com" },
          { label: "Mike Johnson", value: "mike.johnson@example.com" },
          { label: "Sarah Williams", value: "sarah.williams@example.com" },
          { label: "Admin User", value: "admin@example.com" },
        ],
      },
      {
        key: "createdAt",
        label: "Created At",
        type: "dateRange",
      },
      {
        key: "updatedAt",
        label: "Updated At",
        type: "dateRange",
      },
    ],
  };

  const loadProjects = useCallback(async () => {
    try {
      setLoading(true);
      let response;

      // Get client_id from URL params (for chief-admin) or user's assigned client
      const urlClientId = searchParams.get("client_id");
      const storedClientId = getStoredClientId();

      // Check if user is chief-admin (check both role_name and role)
      const userRole = user?.role_name || user?.role;
      const normalizeRole = (role) =>
        role ? role.toLowerCase().replace(/[_\s-]/g, "-") : null;
      const normalizedUserRole = normalizeRole(userRole);
      const isUserChiefAdmin = normalizedUserRole === "chief-admin";

      const filterParams = {};
      filterParams.filters = appliedFilters;

      if (isUserChiefAdmin && urlClientId) {
        // Chief admin viewing specific client's projects - store it
        storeClientId(urlClientId);
        setSelectedClientId(urlClientId);
        // Reset client active status (will be updated by loadClientName)
        setSelectedClientIsActive(true);
        // Fetch client name and active status
        response = await projectService.getProjects({
          page: currentPage,
          limit: itemsPerPage,
          client_id: urlClientId,
          is_active: isActiveFilter,
          sort_by: sortBy,
          sort_order: sortOrder,
          filterParams: filterParams,
          search_term: debouncedSearchTerm,
          search_type: "name",
        });
      } else if (isUserChiefAdmin && !urlClientId) {
        // Chief admin viewing all projects - use stored client_id or user's client_id
        const defaultClientId = storedClientId || user?.client_id;
        if (defaultClientId) {
          storeClientId(defaultClientId);
          setSelectedClientId(defaultClientId);
          // Reset client active status (will be updated by loadClientName)
          setSelectedClientIsActive(true);
          // Fetch client name and active status

          response = await projectService.getProjects({
            page: currentPage,
            limit: itemsPerPage,
            client_id: defaultClientId,
            is_active: isActiveFilter,
            sort_by: sortBy,
            sort_order: sortOrder,
            filterParams: filterParams,
            search_term: debouncedSearchTerm,
            search_type: "name",
          });
        } else {
          setError(
            "Please select a client to view projects. Chief admin must specify a client_id.",
          );
          setProjects([]);
          return;
        }
      } else {
        // Other users (non-chief-admin) see projects based on their assigned client
        // Always use user's client_id from JWT - don't use stored or URL client_id
        const userClientId = user?.client_id;

        if (!userClientId) {
          setError(
            "User is not assigned to any client. Please contact administrator.",
          );
          setProjects([]);
          setLoading(false);
          return;
        }

        // Clear any stored client_id that doesn't match user's actual client_id
        // This prevents using stale/wrong client_id
        if (storedClientId && storedClientId !== userClientId) {
          console.warn(
            `Clearing mismatched stored client_id: ${storedClientId} (user's client_id: ${userClientId})`,
          );
          clearStoredClientId();
        }

        // Use user's actual client_id
        setSelectedClientId(userClientId);
        // Reset client active status (will be updated by loadClientName)
        setSelectedClientIsActive(true);
        // Fetch client name and active status

        // Don't pass client_id — projectService resolves from JWT

        response = await projectService.getProjects({
          page: currentPage,
          limit: itemsPerPage,
          is_active: isActiveFilter,
          sort_by: sortBy,
          sort_order: sortOrder,
          filterParams: filterParams,
          search_term: debouncedSearchTerm,
          search_type: "name",
        });
      }

      // Handle the response structure correctly
      if (response.success) {
        let projectList = [];
        // Check if data is in response.data.projects (backend structure) or directly in response.data
        if (response.data.projects && Array.isArray(response.data.projects)) {
          projectList = [...response.data.projects]; // Create a copy to avoid mutating const
        } else if (Array.isArray(response.data)) {
          projectList = [...response.data]; // Create a copy to avoid mutating const
        }

        // Update pagination info
        if (response.data?.pagination) {
          setTotalProjects(
            response.data.pagination.total || projectList.length,
          );
          setTotalPages(response.data.pagination.total || 1);
        } else {
          setTotalProjects(projectList.length);
          setTotalPages(1);
        }

        setProjects(projectList);
      } else {
        setProjects([]);
        if (response.message) {
          setError(response.message);
        }
      }
    } catch (error) {
      console.error("Error loading projects:", error);

      // Check for 403 Forbidden (unauthorized access)
      if (error.response?.status === 403) {
        setError(
          "Unauthorized Access: You do not have permission to view projects.",
        );
      } else if (error.response?.status === 401) {
        setError("Authentication required. Please login again.");
      } else {
        setError(
          error.response?.data?.message ||
            error.message ||
            "Failed to load projects. Please try again.",
        );
      }

      setProjects([]);
    } finally {
      setLoading(false);
    }
  }, [
    searchParams,
    user?.role_name,
    user?.role,
    user?.client_id,
    currentPage,
    isActiveFilter,
    appliedFilters,
    sortBy,
    sortOrder,
    debouncedSearchTerm,
  ]);

  // Any search edit (from any page) should list from page 1; debounced term applies on the next fetch.
  useEffect(() => {
    setCurrentPage((p) => (p === 1 ? p : 1));
  }, [searchTerm]);

  useEffect(() => {
    fetchFilters();
  }, [fetchFilters]);

  // Single fetch trigger: loadProjects already depends on debounced search, filters,
  // pagination, sort, and URL params. A second effect keyed on loadProjects would run twice.
  useEffect(() => {
    const isOnProjectsPage =
      location.pathname === "/projects" ||
      (location.pathname.startsWith("/projects") &&
        !location.pathname.startsWith("/projects/") &&
        location.pathname !== "/settings");

    if (!isOnProjectsPage) {
      return;
    }

    setError("");
    if (!user) {
      return;
    }

    loadProjects();
  }, [location.pathname, location.search, loadProjects, user]);

  const handleCreateProject = useCallback(() => {
    setShowProjectModal(true);
    const searchParams = new URLSearchParams(location.search);
    navigate(`/create-project?client_id=${searchParams.get("client_id")}`);
  }, []);

  const handleViewProjectPage = useCallback(
    (project) => {
      navigate(
        `/create-project?client_id=${project.client_id}&project_id=${project.project_id}&mode=view`,
      );
    },
    [navigate],
  );

  const handleInviteUser = useCallback(() => {
    const urlClientId = searchParams.get("client_id");
    const storedClientId = getStoredClientId();
    const effectiveClientId =
      urlClientId || storedClientId || user?.client_id || null;
    if (!effectiveClientId) {
      toast.error(
        "No client selected. Choose a client from All Clients first, then try again.",
      );
      return;
    }
    const params = new URLSearchParams();
    params.set("client_id", effectiveClientId);
    params.set("section", "users");
    navigate(`/settings?${params.toString()}`);
  }, [navigate, searchParams, user?.client_id]);

  // Reload when user data changes (e.g., after SSO login refreshes user)

  // Check if we should open the create project modal from URL
  useEffect(() => {
    const action = searchParams.get("action");
    if (action === "create" && canManageProjects) {
      handleCreateProject();
      // Remove the action param from URL
      const newParams = new URLSearchParams(searchParams.toString());
      newParams.delete("action");
      navigate(`/projects?${newParams.toString()}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    searchParams.toString(),
    canManageProjects,
    navigate,
    handleCreateProject,
  ]);

  const handleSort = useCallback(({ field, direction }) => {
    setSortBy(field ?? null);
    setSortOrder(direction ?? null);
  }, []);

  const handleViewProject = (project) => {
    const project_id = project.project_id || project.id;

    // Check if user is chief-admin (check both role_name and role)
    const userRole = user?.role_name || user?.role;
    const normalizeRole = (role) =>
      role ? role.toLowerCase().replace(/[_\s-]/g, "-") : null;
    const normalizedUserRole = normalizeRole(userRole);
    const isUserChiefAdmin = normalizedUserRole === "chief-admin";

    // For chief-admin, use selectedClientId from URL/stored, for others use user's client_id
    const effectiveClientId = isUserChiefAdmin
      ? selectedClientId || searchParams.get("client_id") || getStoredClientId()
      : user?.client_id || selectedClientId;

    if (!effectiveClientId && isUserChiefAdmin) {
      toast.error("Client ID is required. Please select a client first.");
      return;
    }

    if (!effectiveClientId) {
      toast.error("Client ID is required. Please contact administrator.");
      return;
    }

    // Check if project type is MULTI_SUITE or MULTIPLE_TEST_SUITES
    // Check both project_type and structure fields for compatibility
    const projectType = project.project_type || project.type;
    const projectStructure = project.structure;
    const isMultiSuite =
      projectType === "MULTI_SUITE" ||
      projectType === "MULTIPLE_TEST_SUITES" ||
      projectType === "Multiple Test Suites to manage cases" ||
      projectStructure === "MULTI_SUITE" ||
      projectStructure === "MULTIPLE_TEST_SUITES";

    // SINGLE REPOSITORY WITH BASELINE SUPPORT - commented out
    // const isBaselineEnabled =
    //   projectStructure === 'BASELINE_ENABLED' ||
    //   projectStructure === 'SINGLE_REPOSITORY_WITH_BASELINE_AND_BRANCH';

    // Navigate based on project structure
    if (isMultiSuite) {
      navigate(
        `/suites?project_id=${project_id}&project_name=${encodeURIComponent(project.name)}&client_id=${effectiveClientId}`,
      );
      // } else if (isBaselineEnabled) {
      //   navigate(`/baselines?project_id=${project_id}&project_name=${encodeURIComponent(project.name)}&client_id=${effectiveClientId}`);
    } else {
      navigate(
        `/test-cases?project_id=${project_id}&project_name=${encodeURIComponent(project.name)}&client_id=${effectiveClientId}`,
      );
    }
  };

  const handleViewTestCases = (project) => {
    // Check if user has permission to view test cases
    if (!canViewTestCases) {
      toast.error("You do not have permission to view test cases");
      return;
    }
    const project_id = project.project_id || project.id;

    // Check if user is chief-admin (check both role_name and role)
    const userRole = user?.role_name || user?.role;
    const normalizeRole = (role) =>
      role ? role.toLowerCase().replace(/[_\s-]/g, "-") : null;
    const normalizedUserRole = normalizeRole(userRole);
    const isUserChiefAdmin = normalizedUserRole === "chief-admin";

    // For chief-admin, use selectedClientId from URL/stored, for others use user's client_id
    const effectiveClientId = isUserChiefAdmin
      ? selectedClientId || searchParams.get("client_id") || getStoredClientId()
      : user?.client_id || selectedClientId;

    if (!effectiveClientId && isUserChiefAdmin) {
      toast.error("Client ID is required. Please select a client first.");
      return;
    }

    if (!effectiveClientId) {
      toast.error("Client ID is required. Please contact administrator.");
      return;
    }

    navigate(
      `/test-cases?project_id=${project_id}&project_name=${encodeURIComponent(project.name)}&client_id=${effectiveClientId}`,
    );
  };

  const handleViewTestRuns = (project) => {
    const project_id = project.project_id || project.id;
    navigate(
      `/test-runs?project_id=${project_id}&project_name=${encodeURIComponent(project.name)}&client_id=${selectedClientId || user?.client_id}`,
    );
  };

  const handleTogglePriority = async (project, e) => {
    e.stopPropagation(); // Prevent row click

    const projectId = project.project_id || project.id;
    if (!projectId) {
      toast.error("Project ID is missing");
      return;
    }

    const newPriorityValue = !project.is_priority;

    try {
      await projectService.updateProject({
        project_id: projectId,
        is_priority: newPriorityValue,
      });
      toast.success(
        newPriorityValue ? "Project prioritized" : "Project unprioritized",
      );
      await loadProjects();
    } catch (error) {
      console.error("Error toggling project priority:", error);

      toast.error(
        error.response?.data?.message || "Failed to update project priority",
      );
    }
  };

  const confirmDeleteProject = async () => {
    if (!deletingProject) return;

    try {
      setDeleteLoading(true);
      await projectService.deleteProject(
        deletingProject.project_id || deletingProject.id,
      );
      await loadProjects(); // Refresh the list
      setShowDeleteDialog(false);
      setDeletingProject(null);
    } catch (error) {
      console.error("Error deleting project:", error);
      setError("Failed to delete project. Please try again.");
    } finally {
      setDeleteLoading(false);
    }
  };

  const confirmInactiveProject = async () => {
    if (!inactivatingProject) return;
    try {
      setInactiveLoading(true);
      await projectService.deactivateProject(
        inactivatingProject.project_id || inactivatingProject.id,
      );
      await loadProjects(); // Refresh the list
      setShowInactiveDialog(false);
      setInactivatingProject(null);
      toast.success("Project has been marked as inactive");
    } catch (error) {
      console.error("Error deactivating project:", error);
      setError("Failed to mark project as inactive. Please try again.");
      toast.error("Failed to mark project as inactive");
    } finally {
      setInactiveLoading(false);
    }
  };

  const confirmActivateProject = async () => {
    if (!activatingProject) return;
    try {
      setActivateLoading(true);
      await projectService.activateProject(
        activatingProject.project_id || activatingProject.id,
      );
      await loadProjects(); // Refresh the list
      setShowActivateDialog(false);
      setActivatingProject(null);
      toast.success("Project has been marked as active");
    } catch (error) {
      console.error("Error activating project:", error);
      setError("Failed to mark project as active. Please try again.");
      toast.error("Failed to mark project as active");
    } finally {
      setActivateLoading(false);
    }
  };

  const onClickComplete = (project) => {
    setShowCompleteDialog(true);
    setCompletingProject(project);
  };

  const confirmCompleteProject = async () => {
    if (!completingProject) return;
    try {
      setCompleteLoading(true);
      await projectService.completeProject(
        completingProject.project_id || completingProject.id,
        completingProject.client_id,
      );
      await loadProjects(); // Refresh the list
      toast.success("Project has been completed");
      setShowCompleteDialog(false);
      setCompletingProject(null);
    } catch (e) {
    } finally {
      setCompleteLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const hasActiveFilters = () => {
    return Object.keys(appliedFilters).some((key) => {
      const value = appliedFilters[key];
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      if (typeof value === "object" && value !== null) {
        return Object.values(value).some((v) => v);
      }
      return !!value;
    });
  };

  const handleApplyFilters = (filterValues) => {
    setAppliedFilters(filterValues);
    setShowFilter(false);
    setCurrentPage(1); // Reset to first page when filters change
  };

  const handleResetFilters = () => {
    setAppliedFilters({});
    setCurrentPage(1);
  };

  // Table columns
  const columns = [
    {
      key: "priority",
      header: "",
      render: (_, row) => {
        const isPriority = row.is_priority === true;
        return (
          <div className="flex items-center justify-center">
            <Star
              className={`w-4 h-4 cursor-pointer transition-colors ${
                isPriority
                  ? "text-yellow-500 fill-yellow-500"
                  : "text-gray-400 hover:text-yellow-500"
              }`}
              onClick={(e) => handleTogglePriority(row, e)}
            />
          </div>
        );
      },
    },
    {
      key: "name",
      header: "Project Name",
      sortable: false,
      render: (value, row) => (
        <div
          className="font-medium text-text-primary cursor-pointer hover:text-primary-600 transition-colors"
          onClick={() => handleViewProject(row)}
        >
          {value || "Unnamed Project"}
        </div>
      ),
    },
    {
      key: "quick_links",
      header: "Quick Link",
      render: (_, row) => {
        return (
          <div className="flex items-center space-x-2 text-sm flex-wrap gap-1">
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleViewTestCases(row);
              }}
              className="text-primary-600 hover:text-primary-700 hover:underline"
            >
              {row.test_case_count ?? 0} Test Cases
            </button>
            <span className="text-text-tertiary">|</span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleViewTestRuns(row);
              }}
              className="text-primary-600 hover:text-primary-700 hover:underline"
            >
              {row.test_run_count ?? 0} Test Run
            </button>
            {/* SINGLE REPOSITORY WITH BASELINE SUPPORT - commented out: Baselines | Branches buttons
            {(projectStructure === 'BASELINE_ENABLED' || projectStructure === 'SINGLE_REPOSITORY_WITH_BASELINE_AND_BRANCH') && (
              <> ... </>
            )}
            */}
          </div>
        );
      },
    },
    {
      key: "created_by",
      header: "Created By",
      render: (value) => {
        if (!value) return "-";
        return (
          <div className="flex items-center space-x-2">
            <User className="w-4 h-4 text-text-tertiary" />
            <span>{formatName(value)}</span>
          </div>
        );
      },
    },
    {
      key: "created_at",
      header: "Created At",
      render: (value) => {
        return formatDate(value);
      },
    },
    {
      key: "updated_at",
      header: "Updated At",
      render: (value) => formatDate(value),
    },
    {
      key: "actions",
      header: "Actions",
      render: (_, row) => {
        const hasActions =
          canEditProject || canDeleteProject || canInactiveProject;
        if (!hasActions) {
          return <span className="text-text-tertiary">-</span>;
        }
        return (
          <>
            <div className="flex items-center justify-center">
              <div className="relative">
                <button
                  ref={(el) => {
                    if (el) actionButtonRefs.current[row.project_id] = el;
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (actionMenuOpen === row.project_id) {
                      setActionMenuOpen(null);
                    } else {
                      const rect = e.currentTarget.getBoundingClientRect();
                      const viewportHeight = window.innerHeight;
                      const buttonCenterY = rect.top + rect.height / 2;
                      const showAbove = buttonCenterY > viewportHeight / 2;

                      setMenuPosition({
                        top: showAbove ? rect.top - 4 : rect.bottom + 4,
                        right: window.innerWidth - rect.right,
                        showAbove: showAbove,
                      });
                      setActionMenuOpen(row.project_id);
                    }
                  }}
                  className="p-2 text-text-tertiary hover:text-primary-600 hover:bg-primary-50 rounded-md transition-colors"
                >
                  <MoreVertical className="w-4 h-4" />
                </button>
              </div>
            </div>
            {actionMenuOpen === row.project_id && (
              <>
                <div
                  className="fixed w-48 bg-white rounded-md shadow-lg z-[9999] border border-border-primary"
                  style={{
                    ...(menuPosition.showAbove
                      ? { bottom: `${window.innerHeight - menuPosition.top}px` }
                      : { top: `${menuPosition.top}px` }),
                    right: `${menuPosition.right}px`,
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="py-1">
                    <button
                      onClick={() => handleViewProjectPage(row)}
                      className="w-full text-left px-4 py-2 text-sm text-text-primary hover:bg-bg-hover flex items-center space-x-2"
                    >
                      <EyeIcon className="w-4 h-4" />
                      <span>View</span>
                    </button>
                    {canEditProject &&
                      row.is_active !== false &&
                      selectedClientIsActive && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(
                              `/create-project?client_id=${row.client_id}&project_id=${row.project_id}&mode=edit`,
                            );
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-text-primary hover:bg-bg-hover flex items-center space-x-2"
                        >
                          <Pencil className="w-4 h-4" />
                          <span>Edit</span>
                        </button>
                      )}
                    {canEditProject && row.is_active !== false && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(
                            `/create-project?client_id=${row.client_id}&project_id=${row.project_id}&mode=add_user`,
                          );
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-text-primary hover:bg-bg-hover flex items-center space-x-2"
                      >
                        <UserPlus className="w-4 h-4" />
                        <span>Add User</span>
                      </button>
                    )}
                    {canEditProject && row.is_active !== false && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setActionMenuOpen(null);
                          onClickComplete(row);
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-text-primary hover:bg-bg-hover flex items-center space-x-2"
                      >
                        <CheckCheckIcon className="w-4 h-4" />
                        <span>Complete Project</span>
                      </button>
                    )}
                    {canInactiveProject && row.is_active !== false && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setInactivatingProject(row);
                          setShowInactiveDialog(true);
                          setActionMenuOpen(null);
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-orange-600 hover:bg-orange-50 flex items-center space-x-2"
                      >
                        <Calendar className="w-4 h-4" />
                        <span>Mark Inactive</span>
                      </button>
                    )}

                    {canInactiveProject && row.is_active === false && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setActivatingProject(row);
                          setShowActivateDialog(true);
                          setActionMenuOpen(null);
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-green-600 hover:bg-green-50 flex items-center space-x-2"
                      >
                        <Calendar className="w-4 h-4" />
                        <span>Mark Active</span>
                      </button>
                    )}
                    {canDeleteProject && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeletingProject(row);
                          setShowDeleteDialog(true);
                          setActionMenuOpen(null);
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center space-x-2"
                      >
                        <Pencil className="w-4 h-4" />
                        <span>Delete</span>
                      </button>
                    )}
                  </div>
                </div>
              </>
            )}
          </>
        );
      },
    },
  ];

  // Grid view component
  const ProjectGridCard = ({ project }) => {
    const projectId = project.project_id || project.id;

    const initials = getInitials(project.name || "Project");
    const hasActions = canEditProject || canDeleteProject || canInactiveProject;

    return (
      <div className="bg-white rounded-lg border border-border-primary p-4 hover:shadow-md transition-shadow relative flex flex-col items-center w-[280px] h-[212px] overflow-hidden shrink-0">
        <button
          type="button"
          aria-label={
            project.is_priority === true
              ? "Remove project priority"
              : "Prioritize project"
          }
          className="absolute top-3 left-3 z-10 p-0.5 rounded hover:bg-gray-50"
          onClick={(e) => handleTogglePriority(project, e)}
        >
          <Star
            className={`w-4 h-4 cursor-pointer transition-colors ${
              project.is_priority === true
                ? "text-yellow-500 fill-yellow-500"
                : "text-gray-400 hover:text-yellow-500"
            }`}
          />
        </button>
        {hasActions && (
          <>
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (actionMenuOpen === projectId) {
                  setActionMenuOpen(null);
                } else {
                  const rect = e.currentTarget.getBoundingClientRect();
                  const viewportHeight = window.innerHeight;
                  const buttonCenterY = rect.top + rect.height / 2;
                  const showAbove = buttonCenterY > viewportHeight / 2;

                  setMenuPosition({
                    top: showAbove ? rect.top - 4 : rect.bottom + 4,
                    right: window.innerWidth - rect.right,
                    showAbove: showAbove,
                  });
                  setActionMenuOpen(projectId);
                }
              }}
              className="absolute top-3 right-3 p-1 text-text-tertiary hover:text-primary-600 rounded"
            >
              <MoreVertical className="w-4 h-4" />
            </button>
            {actionMenuOpen === projectId && (
              <>
                <div
                  className="fixed w-48 bg-white rounded-md shadow-lg z-[9999] border border-border-primary"
                  style={{
                    ...(menuPosition.showAbove
                      ? { bottom: `${window.innerHeight - menuPosition.top}px` }
                      : { top: `${menuPosition.top}px` }),
                    right: `${menuPosition.right}px`,
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="py-1">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(
                          `/create-project?client_id=${project.client_id}&project_id=${project.project_id}&mode=view`,
                        );
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-text-primary hover:bg-bg-hover flex items-center space-x-2"
                    >
                      <EyeIcon className="w-4 h-4" />
                      <span>View</span>
                    </button>
                    {canEditProject &&
                      project.is_active !== false &&
                      selectedClientIsActive && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(
                              `/create-project?client_id=${project.client_id}&project_id=${project.project_id}&mode=edit`,
                            );
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-text-primary hover:bg-bg-hover flex items-center space-x-2"
                        >
                          <Pencil className="w-4 h-4" />
                          <span>Edit</span>
                        </button>
                      )}
                    {canEditProject && project.is_active !== false && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(
                            `/create-project?client_id=${project.client_id}&project_id=${project.project_id}&mode=add_user`,
                          );
                        }}
                      >
                        <UserPlus className="w-4 h-4" />
                        <span>Add User</span>
                      </button>
                    )}
                    {canEditProject && project.is_active !== false && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onClickComplete(project);
                          setActionMenuOpen(null);
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-text-primary hover:bg-bg-hover flex items-center space-x-2"
                      >
                        <CheckCheckIcon className="w-4 h-4" />
                        <span>Complete Project</span>
                      </button>
                    )}
                    {canInactiveProject && project.is_active !== false && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setInactivatingProject(project);
                          setShowInactiveDialog(true);
                          setActionMenuOpen(null);
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-orange-600 hover:bg-orange-50 flex items-center space-x-2"
                      >
                        <Calendar className="w-4 h-4" />
                        <span>Mark Inactive</span>
                      </button>
                    )}
                    {canInactiveProject && project.is_active === false && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setActivatingProject(project);
                          setShowActivateDialog(true);
                          setActionMenuOpen(null);
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-green-600 hover:bg-green-50 flex items-center space-x-2"
                      >
                        <Calendar className="w-4 h-4" />
                        <span>Mark Active</span>
                      </button>
                    )}
                    {canDeleteProject && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeletingProject(project);
                          setShowDeleteDialog(true);
                          setActionMenuOpen(null);
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center space-x-2"
                      >
                        <Pencil className="w-4 h-4" />
                        <span>Delete</span>
                      </button>
                    )}
                  </div>
                </div>
              </>
            )}
          </>
        )}
        <div
          className="flex flex-col items-center text-center cursor-pointer"
          onClick={() => handleViewProject(project)}
        >
          <div className="flex flex-row items-center self-start justify-start w-full mb-2">
            <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center mb-3 mr-4">
              <span className="text-gray-600 font-semibold text-sm">
                {initials}
              </span>
            </div>
            <div className="flex flex-col space-y-1 items-start justify-start">
              <h3 className="font-medium text-text-primary mb-1 truncate w-full text-left">
                {project.name || "Unnamed Project"}
              </h3>

              <h3 className="font-semibold text-text-primary mb-2">
                {project.created_by || "-"}
              </h3>
            </div>
          </div>
          <div className="flex flex-col space-y-2 mb-2">
            <div className="flex flex-row space-x-4 text-sm text-text-secondary gap-1 mb-2">
              <div className="flex flex-col items-start justify-start font-semibold text-black">
                <span>Created At:</span>
                <div className="flex flex-row items-center font-medium">
                  <Calendar className="w-4 h-4 mr-1" />
                  <span>{formatDate(project.created_at) || "-"}</span>
                </div>
              </div>
              <div className="flex flex-col items-start justify-start font-semibold text-black">
                <span>Updated At:</span>
                <div className="flex flex-row items-center font-medium">
                  <Calendar className="w-4 h-4 mr-1" />
                  <span>{formatDate(project.updated_at) || "-"}</span>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-2 text-sm text-text-secondary flex-wrap gap-1 justify-center my-2 font-semibold">
              <h3>{project.test_case_count ?? 0}</h3>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleViewTestCases(project);
                }}
                className="text-[#379Ae6ff] hover:underline font-medium"
              >
                Test Cases
              </button>
              <span>|</span>
              <h3>{project.test_run_count ?? 0}</h3>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleViewTestRuns(project);
                }}
                className="text-[#379Ae6ff] hover:underline font-medium"
              >
                Test Run
              </button>
            </div>
            {/* SINGLE REPOSITORY WITH BASELINE SUPPORT - commented out
            {(project.structure === 'BASELINE_ENABLED' || project.structure === 'SINGLE_REPOSITORY_WITH_BASELINE_AND_BRANCH') && (
              <> Baselines | Branches buttons </>
            )}
            */}
            <h3 className="w-full font-semibold text-text-primary mb-2">
              Updated By: {project.updated_by || "-"}
            </h3>
          </div>
        </div>
      </div>
    );
  };

  const TitleCard = () => {
    return (
      <div className="grid grid-cols-4 gap-6">
        <ProjectTitleCard
          title="Total Projects"
          count={totalProjects}
          icon={<Briefcase className="w-5 h-5 text-gray-600" />}
        />
        <ProjectTitleCard
          title="Completed Projects"
          count={totalProjects}
          icon={<Briefcase className="w-5 h-5 text-gray-600" />}
          textColor="#197DCAFF"
          bgColor={"#ACD5F5FF"}
        />
        <ProjectTitleCard
          title="Total Test Cases"
          count={totalProjects}
          icon={<BookOpenText className="w-5 h-5 text-gray-600" />}
        />
        <ProjectTitleCard
          title="Total Test Runs"
          count={totalProjects}
          icon={<LucidePlayCircle className="w-5 h-5 text-gray-600" />}
          textColor="#197DCAFF"
          bgColor="#ACD5F5FF"
        />
      </div>
    );
  };

  return (
    <div
      className="space-y-6 -m-2 p-6 min-h-[calc(100vh-120px)]"
      style={{ backgroundColor: "#F5F4F4FF" }}
    >
      {/* Page Header */}
      <div className="flex justify-between items-start">
        <div className="flex flex-col space-y-1">
          <h1 className="text-2xl font-bold text-text-primary">All Projects</h1>
          <h2 className="text-sm text-text-secondary">
            A project is a collection of related test cases
          </h2>
          {/* <Info className="w-4 h-4 text-text-tertiary" /> */}
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setViewMode(viewMode === "table" ? "grid" : "table")}
            className="flex items-center space-x-2 px-4 py-2 bg-bg-primary border border-border-primary rounded-lg hover:bg-bg-hover transition-colors"
          >
            {viewMode === "table" ? (
              <>
                <Grid3x3 className="w-4 h-4" />
                <span>Grid View</span>
              </>
            ) : (
              <>
                <Table2 className="w-4 h-4" />
                <span>Table View</span>
              </>
            )}
          </button>
          {canManageProjects && (
            <button
              onClick={handleCreateProject}
              className="btn btn-success flex items-center space-x-2 "
            >
              <Plus className="w-4 h-4" />
              <span>Add New Project</span>
            </button>
          )}
          {canManageProjects && (
            <button
              type="button"
              onClick={() => setShowSampleProjectModal(true)}
              className="bg-[#F3FBF8FF] text-green-600 border border-green-600 flex items-center space-x-2 px-4 py-2 rounded"
            >
              <Plus className="w-4 h-4" />
              <span>Add Sample Project</span>
            </button>
          )}
        </div>
      </div>

      <CreateSampleProjectModal
        open={showSampleProjectModal}
        onOpenChange={setShowSampleProjectModal}
        clientId={sampleEffectiveClientId}
        onSuccess={() => loadProjects()}
      />

      {/* Error Message */}
      {error && (
        <div
          className={`rounded-lg p-4 ${
            error.includes("Unauthorized Access") ||
            error.includes("Authentication required")
              ? "bg-red-50 border-2 border-red-300"
              : "bg-red-50 border border-red-200"
          }`}
        >
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <p
                className={`font-medium ${
                  error.includes("Unauthorized Access") ||
                  error.includes("Authentication required")
                    ? "text-red-700 text-base"
                    : "text-red-600 text-sm"
                }`}
              >
                {error}
              </p>
            </div>
            <button
              onClick={() => setError("")}
              className="text-red-600 hover:text-red-800 text-sm font-medium ml-4"
              aria-label="Dismiss error"
            >
              ×
            </button>
          </div>
        </div>
      )}
      {/* <TitleCard /> */}

      {/* Project List Section */}
      <div className="space-y-4 mt-10">
        <div className="flex items-center justify-between gap-4">
          <div className="relative flex-1 w-1/2">
            <input
              type="text"
              placeholder="Search with Project Name…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-4 pr-10 py-2 border border-border-primary rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 w-full"
            />
            <span className="absolute top-0 bottom-0 right-0 flex items-center justify-center w-12 bg-[#C5BFC1FF] rounded-r-lg">
              <Search className="w-5 h-5 text-text-tertiary" />
            </span>
          </div>
          <div className="flex items-center space-x-4">
            {/* Active/Inactive Toggle */}
            <div className="flex items-center space-x-2 bg-bg-secondary rounded-lg p-0">
              <button
                onClick={() => setIsActiveFilter(true)}
                className={`px-4 py-1 rounded text-sm font-medium transition-colors ${
                  isActiveFilter
                    ? "bg-green-500 text-white"
                    : "text-text-secondary hover:text-text-primary"
                }`}
              >
                Active
              </button>
              <button
                onClick={() => setIsActiveFilter(false)}
                className={`px-4 py-1 rounded text-sm font-medium transition-colors ${
                  !isActiveFilter
                    ? "bg-gray-500 text-white"
                    : "text-text-secondary hover:text-text-primary"
                }`}
              >
                Inactive
              </button>
              <Stack flexDirection={"row"} gap={"2px"}>
                <MuiSelect
                  label="Sort By"
                  value={sortBy}
                  onFilterChange={setSortBy}
                  options={[
                    { label: "Project Name", value: "name" },
                    { label: "Updated At", value: "updated_at" },
                    { label: "Created At", value: "created_at" },
                  ]}
                  sx={{ minWidth: "100px" }}
                  className="bg-white"
                />
                <button
                  onClick={() =>
                    setSortOrder((prev) => (prev === "desc" ? "asc" : "desc"))
                  }
                  title={
                    sortOrder === "desc"
                      ? "Descending — click to switch to Ascending"
                      : "Ascending — click to switch to Descending"
                  }
                  className="flex items-center justify-center w-10 h-[38px] border border-gray-300 rounded-md bg-white hover:bg-gray-50 transition-colors flex-shrink-0"
                >
                  {sortOrder === "desc" ? (
                    <ArrowDownWideNarrow className="w-5 h-5 text-gray-700" />
                  ) : (
                    <ArrowUpWideNarrow className="w-5 h-5 text-gray-700" />
                  )}
                </button>
              </Stack>
              <Button
                variant="outline"
                className="relative"
                style={{
                  borderColor: hasActiveFilters() ? "#2E9E6F" : undefined,
                  background: hasActiveFilters() ? "#f0fdf4" : undefined,
                }}
                onClick={() => setShowFilter(true)}
              >
                <Filter className="w-4 h-4 mr-2" />
                Filters
                {hasActiveFilters() && (
                  <span className="ml-2 bg-[#2E9E6F] text-white text-xs px-2 py-0.5 rounded-full">
                    {
                      Object.keys(appliedFilters).filter((key) => {
                        const value = appliedFilters[key];
                        if (Array.isArray(value)) return value.length > 0;
                        if (typeof value === "object")
                          return Object.values(value).some((v) => v);
                        return !!value;
                      }).length
                    }
                  </span>
                )}
              </Button>
              {canInviteUser && (
                <button
                  type="button"
                  onClick={handleInviteUser}
                  className="flex items-center gap-2 px-4 py-1 rounded-xl border-2 border-blue-500 text-blue-600 bg-white hover:bg-blue-50 transition-colors"
                >
                  <CircleUser className="w-4 h-4" />
                  Invite User
                </button>
              )}
            </div>
          </div>
        </div>

        {projects.length === 0 && !loading ? (
          <ProjectPageEmptyState
            isProjectActive={isActiveFilter}
            onCreateNewProject={handleCreateProject}
            onCreateSampleProject={() => setShowSampleProjectModal(true)}
            canCreateNewProject={canManageProjects}
            canCreateSampleProject={canManageProjects}
          />
        ) : (
          <div>
            {/* Table View */}
            {viewMode === "table" && (
              <>
                <div className="card overflow-hidden">
                  <DataTable
                    data={projects}
                    columns={columns}
                    loading={loading}
                    emptyMessage="No projects found. Create your first project to get started."
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSort={handleSort}
                    showTotalCount={true}
                    totalCount={totalProjects}
                  />
                </div>
              </>
            )}

            {/* Grid View */}
            {viewMode === "grid" && (
              <div className="py-4">
                {loading ? (
                  <div className="flex flex-wrap gap-6 justify-start py-4">
                    <CardSkeleton className="w-[280px]" />
                    <CardSkeleton className="w-[280px]" />
                    <CardSkeleton className="w-[280px]" />
                  </div>
                ) : projects.length === 0 && !canManageProjects ? (
                  <div className="min-h-[200px] flex items-center justify-center text-text-tertiary">
                    No projects found. Create your first project to get started.
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-6 justify-start">
                    {canManageProjects && (
                      <div
                        onClick={handleCreateProject}
                        className="bg-[#E4E0E1FF] rounded-lg p-8 cursor-pointer transition-colors flex flex-col items-center justify-center w-[280px] h-[212px] shrink-0"
                      >
                        <div className="rounded-full bg-[#F5F4F4FF] p-3 mb-2 flex items-center justify-center">
                          <Plus
                            className="w-12 h-12 text-[#504B4D]"
                            fontWeight="bold"
                          />
                        </div>
                        <span className="text-[#504B4D] font-bold text-[24px]">
                          New Project
                        </span>
                      </div>
                    )}

                    {projects.map((project) => (
                      <ProjectGridCard
                        key={project.project_id || project.id}
                        project={project}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
            <CustomFilter
              config={filters}
              onApply={handleApplyFilters}
              onReset={handleResetFilters}
              initialFilters={appliedFilters}
              open={showFilter}
              onClose={() => setShowFilter(false)}
            />

            {/* Pagination */}
            <div className="flex justify-between items-center">
              <div className="flex justify-start items-center space-x-2 mt-4 ml-2">
                <span>
                  Showing {Math.min(itemsPerPage * currentPage, totalPages)} of{" "}
                  {totalProjects}
                </span>
              </div>
              {totalPages > 1 && (
                <div className="flex justify-end items-center space-x-2 mt-6">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="px-3 py-2 rounded-lg bg-bg-primary border border-border-primary hover:bg-bg-hover disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  {Array.from(
                    { length: Math.ceil(totalPages / itemsPerPage) },
                    (_, i) => i + 1,
                  ).map((page) => (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`px-4 py-2 rounded-lg ${
                        currentPage === page
                          ? "bg-primary-600 text-white"
                          : "bg-bg-primary border border-border-primary hover:bg-bg-hover"
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="px-3 py-2 rounded-lg bg-bg-primary border border-border-primary hover:bg-bg-hover disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        {canDeleteProject && (
          <ConfirmDialog
            isOpen={showDeleteDialog}
            onClose={() => {
              setShowDeleteDialog(false);
              setDeletingProject(null);
            }}
            onConfirm={confirmDeleteProject}
            title="Delete Project"
            message={`Are you sure you want to delete "${deletingProject?.name}"? This action cannot be undone and will also delete all associated test cases.`}
            confirmText="Delete Project"
            confirmVariant="danger"
            loading={deleteLoading}
          />
        )}

        {/* Inactive Confirmation Dialog */}
        {canInactiveProject && (
          <ConfirmDialog
            isOpen={showInactiveDialog}
            onClose={() => {
              setShowInactiveDialog(false);
              setInactivatingProject(null);
            }}
            onConfirm={confirmInactiveProject}
            title="Mark Project as Inactive"
            message={`Are you sure you want to mark "${inactivatingProject?.name}" as inactive? The project will appear in the inactive tab.`}
            confirmText="Mark Inactive"
            cancelText="Cancel"
            loading={inactiveLoading}
          />
        )}

        {/* Activate Confirmation Dialog */}
        {canEditProject && (
          <ConfirmDialog
            isOpen={showActivateDialog}
            onClose={() => {
              setShowActivateDialog(false);
              setActivatingProject(null);
            }}
            onConfirm={confirmActivateProject}
            title="Mark Project as Active"
            message={`Are you sure you want to mark "${activatingProject?.name}" as active? The project will appear in the active tab.`}
            confirmText="Mark Active"
            cancelText="Cancel"
            loading={activateLoading}
          />
        )}
        {canEditProject && (
          <ConfirmDialog
            isOpen={showCompleteDialog}
            onClose={() => {
              setShowCompleteDialog(false);
            }}
            onConfirm={confirmCompleteProject}
            title="Complete Project?"
            message={`This will lock the project and make it read-only. You won't be able to modify or add new data after completion.`}
            confirmText="Complete Project"
            confirmVariant="danger"
            loading={completeLoading}
          />
        )}

        {/* Click outside to close action menu */}
        {actionMenuOpen && (
          <div
            className="fixed inset-0 z-[9998]"
            onClick={() => setActionMenuOpen(null)}
          />
        )}
      </div>
    </div>
  );
};

export default ProjectsPage;
